import { useIntl } from "react-intl";

import { PricingFeatureTable } from "@/components/wpl/pricing/src/features-table";
import {
    QuestionsCta,
    AtlassianGuardAddOnShared
} from "@/components/wpl/pricing/src/index.ts";
import { usePricingContentContext } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import {
    PricingFaq,
    QuotationsAdornment
} from "@/components/wpl/pricing/src/pricing-faq/index.ts";
import { PricingPageLayout } from "@/components/wpl/pricing/src/pricing-page-layout/index.ts";
import { useLocalizedHref } from "@/libraries/web-framework";
import { GlobalNav } from "@/patterns/global-nav/src/nav-global.tsx";
import {
    CallToAction,
    PricingContent
} from "@/utils/pricing-queries/get-pricing-content-gql.ts";
import { ParentScrollTracking } from "@/utils/scroll-tracking/parent-scroll-tracker.tsx";
import { UserLocale } from "@/utils/useGeoCurrency.ts";

import { AppPricingData } from "../../../../contentful/index.tsx";
import { CollectionsCompoundSubNavProps } from "../../collections-compound-nav-props.tsx";

import { Hero } from "./sections/hero";
import { TeamworkCollectionPriceFinder } from "./sections/teamwork-collection-price-finder.tsx";

export interface TeamworkCollectionPricingPageTemplateProps {
    pricing?: AppPricingData;
    countryCode?: UserLocale;
    pricingContent?: PricingContent;
}

// eslint-disable-next-line complexity
export const TeamworkCollectionPricingPageTemplate = (
    props: TeamworkCollectionPricingPageTemplateProps
) => {
    const intl = useIntl();

    const content = usePricingContentContext();

    const featuresCtaContent = content
        ?.callToActionContainer?.[1] as CallToAction;
    const questionsCtaContent = content
        ?.callToActionContainer?.[0] as CallToAction;

    const teamworkCollectionsPricingNav = (
        <GlobalNav
            productKey={"home"}
            compoundNav={{
                ...CollectionsCompoundSubNavProps(),
                ctaUrl: "/collections/teamwork#contact"
            }}
        />
    );

    return (
        <PricingPageLayout
            nav={teamworkCollectionsPricingNav}
            showFallback={content?.hasError}
            hero={<Hero />}
        >
            <ParentScrollTracking
                scrollTrackingEnabled={true}
                pageId="teamwork-collection-pricing-page"
            >
                <TeamworkCollectionPriceFinder
                    pricing={props.pricing}
                    countryCode={props.countryCode}
                />
                <PricingFeatureTable
                    title={content?.compareFeaturesTitle ?? undefined}
                    comparePlans={content?.comparePlans ?? undefined}
                    compareFeatures={content?.compareFeatures ?? undefined}
                    linkValues={{
                        linkText: featuresCtaContent?.text ?? undefined,
                        linkHref: featuresCtaContent?.url ?? undefined
                    }}
                    dataProductKey="teamworkCollection"
                />
                <AtlassianGuardAddOnShared
                    headingText={intl.formatMessage({
                        id: "TeamworkCollection.pricing.atlassianGuard.heading",
                        defaultMessage:
                            "Teamwork Collection Premium and Enterprise include Atlassian Guard Standard"
                    })}
                    bodyText={""}
                    ctaHref={useLocalizedHref("/software/guard")}
                />
                <QuotationsAdornment>
                    <ParentScrollTracking>
                        <PricingFaq
                            header={content?.getMoreDetailsTitle ?? undefined}
                            planDetails={
                                content?.additionalDetails ?? undefined
                            }
                        />
                    </ParentScrollTracking>
                </QuotationsAdornment>
                <QuestionsCta
                    product={"Teamwork Collection"}
                    ctaHref={questionsCtaContent?.url ?? undefined}
                    questionsHeader={questionsCtaContent?.title ?? undefined}
                    priceAndLicensingLinkText={
                        questionsCtaContent?.text ?? undefined
                    }
                    questionsBody={
                        questionsCtaContent?.valueProposition ?? undefined
                    }
                    imageHref={questionsCtaContent?.asset?.url}
                />
            </ParentScrollTracking>
        </PricingPageLayout>
    );
};
